/**
 * FinalChecksConfigModal Component
 *
 * Modal for editing final check items configuration from the workflow screen.
 * Allows users to modify which check items are tracked (add, edit, delete, reorder).
 * Includes "Apply to future sessions" checkbox to optionally update global settings.
 *
 * Key behaviors:
 * - Manual Save/Cancel (not auto-save) - config changes are significant
 * - Checkbox always visible; default checked if draft matched global at open, unchecked if divergent
 * - "Reset to defaults" button visible when draft was divergent at modal open
 * - Alert shown when draft was divergent at modal open (not triggered by active edits)
 * - Saves to draft config first, optionally to global settings if checkbox checked
 * - Warnings (duplicate labels) are non-blocking; only errors prevent save
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Checkbox,
  Card,
  CardBody,
  SortableList,
  DragHandle,
  EmptyState,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { generateUUID } from '@/utils';
import { sessionDraftService } from '@/services/SessionDraftService';
import { preTradingSettingsService } from '@/services/PreTradingSettingsService';
import { computeFinalCheckItemsHash } from '../utils/sessionDraftUtils';
import type { PreTradingConfigSnapshot } from '@/types/transient/SessionDraft';
import type { PreTradingSettings } from '@/types/schema/PreTradingSettings';
import type { FinalCheckEntry } from './FinalChecksStep';

// ============================================================================
// Types
// ============================================================================

/**
 * Final check item for local editing state
 */
interface FinalCheckItem {
  id: string;
  label: string;
}

/**
 * Blocking validation errors (prevent save)
 */
interface ItemValidationErrors {
  label?: string;
}

/**
 * Non-blocking warnings (shown but don't prevent save)
 */
interface ItemValidationWarnings {
  label?: string;
}

export interface FinalChecksConfigModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Session draft ID */
  draftId: string;
  /** Current draft config snapshot */
  draftConfig: PreTradingConfigSnapshot;
  /** Current global settings (should be fresh when modal opens) */
  currentSettings: PreTradingSettings;
  /** Callback on successful save with updated config and migrated final checks */
  onSaveSuccess: (
    updatedConfig: PreTradingConfigSnapshot,
    migratedFinalChecks: FinalCheckEntry[] | undefined
  ) => void;
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Validate a single item
 * Returns separate errors (blocking) and warnings (non-blocking)
 */
function validateItem(
  item: FinalCheckItem,
  allItems: FinalCheckItem[]
): { errors: ItemValidationErrors; warnings: ItemValidationWarnings } {
  const errors: ItemValidationErrors = {};
  const warnings: ItemValidationWarnings = {};
  const trimmedLabel = item.label.trim();

  // Label required (blocking error)
  if (!trimmedLabel) {
    errors.label = 'Label is required';
  } else {
    // Check for duplicate labels (warning only, not blocking)
    const isDuplicate = allItems.some(
      (i) =>
        i.id !== item.id &&
        i.label.trim().toLowerCase() === trimmedLabel.toLowerCase()
    );
    if (isDuplicate) {
      warnings.label = 'Duplicate label';
    }
  }

  return { errors, warnings };
}

/**
 * Check if any item has blocking errors (errors only, NOT warnings)
 */
function hasBlockingErrors(
  validationErrors: Record<string, ItemValidationErrors>
): boolean {
  return Object.values(validationErrors).some(
    (itemErrors) => Object.keys(itemErrors).length > 0
  );
}

/**
 * Compare local items against current settings to detect divergence.
 * Used ONLY for computing wasInitiallyDivergent on modal open.
 */
function isFinalCheckItemsDivergent(
  localItems: FinalCheckItem[],
  currentSettings: PreTradingSettings
): boolean {
  const currentItemsList = currentSettings.finalCheckItems;

  // Different count = divergent
  if (localItems.length !== currentItemsList.length) {
    return true;
  }

  // Build map of current items by id
  const currentById = new Map(currentItemsList.map((i) => [i.id, i]));

  // Check each local item exists in current with same properties
  for (const localItem of localItems) {
    const currentItem = currentById.get(localItem.id);
    if (!currentItem) {
      return true;
    }
    if (localItem.label !== currentItem.label) {
      return true;
    }
  }

  return false;
}

// ============================================================================
// Component
// ============================================================================

export const FinalChecksConfigModal: React.FC<FinalChecksConfigModalProps> = ({
  isOpen,
  onClose,
  draftId,
  draftConfig,
  currentSettings,
  onSaveSuccess,
}) => {
  const { showToast } = useToastNotification();

  // Local editing state
  const [localItems, setLocalItems] = useState<FinalCheckItem[]>([]);
  const [applyToFuture, setApplyToFuture] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, ItemValidationErrors>
  >({});
  const [validationWarnings, setValidationWarnings] = useState<
    Record<string, ItemValidationWarnings>
  >({});

  // Track whether draft was divergent from global when modal opened (snapshot, not live)
  const [wasInitiallyDivergent, setWasInitiallyDivergent] = useState(false);

  // Track previous isOpen state to detect open transition (false → true)
  const wasOpenRef = useRef(false);

  // Track newly added item ID for auto-focus
  const newlyAddedIdRef = useRef<string | null>(null);

  // Clear auto-focus ref after render
  useEffect(() => {
    if (newlyAddedIdRef.current === null) return;

    const timeoutId = setTimeout(() => {
      newlyAddedIdRef.current = null;
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [localItems]);

  // Compute dirty state using stable JSON comparison
  const baselineKeyRef = useRef<string>('');
  const localItemsKey = useMemo(() => JSON.stringify(localItems), [localItems]);
  const isDirty = localItemsKey !== baselineKeyRef.current;

  // Initialize local state from draftConfig when modal opens
  // Guard ensures we only initialize on open transition, not when props change while open
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Deep copy items
      const itemsCopy: FinalCheckItem[] = draftConfig.finalCheckItems.map((i) => ({
        id: i.id,
        label: i.label,
      }));

      setLocalItems(itemsCopy);
      baselineKeyRef.current = JSON.stringify(itemsCopy);
      setValidationErrors({});
      setValidationWarnings({});
      setIsSaving(false);
      newlyAddedIdRef.current = null;

      // Compute initial divergence from draftConfig vs currentSettings (not local state)
      const initiallyDivergent = isFinalCheckItemsDivergent(
        itemsCopy,
        currentSettings
      );
      setWasInitiallyDivergent(initiallyDivergent);

      // Checkbox default: checked if draft matched global at open, unchecked if divergent
      setApplyToFuture(!initiallyDivergent);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, draftConfig, currentSettings]);

  // ============================================================================
  // Validation
  // ============================================================================

  const validateAllItems = useCallback((itemsToValidate: FinalCheckItem[]) => {
    const errors: Record<string, ItemValidationErrors> = {};
    const warnings: Record<string, ItemValidationWarnings> = {};

    itemsToValidate.forEach((item) => {
      const result = validateItem(item, itemsToValidate);
      if (Object.keys(result.errors).length > 0) {
        errors[item.id] = result.errors;
      }
      if (Object.keys(result.warnings).length > 0) {
        warnings[item.id] = result.warnings;
      }
    });

    setValidationErrors(errors);
    setValidationWarnings(warnings);
    return { errors, warnings };
  }, []);

  // ============================================================================
  // Item Handlers
  // ============================================================================

  /**
   * Handle inline label change - clears error on change
   */
  const handleItemChange = useCallback((id: string, label: string) => {
    setLocalItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, label } : item))
    );

    // Clear validation error when user starts typing (will re-validate on blur/save)
    setValidationErrors((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return prev;
    });
  }, []);

  /**
   * Handle blur - trim label and validate synchronously
   * Also clears duplicate warnings when label is fixed
   */
  const handleItemBlur = useCallback(
    (id: string, currentLabel: string) => {
      const trimmedLabel = currentLabel.trim();

      // Update item with trimmed label
      setLocalItems((prev) => {
        const updated = prev.map((item) =>
          item.id === id ? { ...item, label: trimmedLabel } : item
        );

        // Re-validate all items to update duplicate warnings
        validateAllItems(updated);

        return updated;
      });
    },
    [validateAllItems]
  );

  /**
   * Add new empty item with auto-focus
   */
  const handleAddItem = useCallback(() => {
    const newId = generateUUID();
    const newItem: FinalCheckItem = {
      id: newId,
      label: '',
    };

    setLocalItems((prev) => {
      const updated = [...prev, newItem];
      validateAllItems(updated);
      return updated;
    });

    // Track for auto-focus
    newlyAddedIdRef.current = newId;
  }, [validateAllItems]);

  /**
   * Delete item immediately
   */
  const handleDeleteItem = useCallback(
    (id: string) => {
      setLocalItems((prev) => {
        const updated = prev.filter((item) => item.id !== id);

        // Re-validate remaining items (duplicate warnings may clear)
        validateAllItems(updated);

        return updated;
      });

      // Clear validation state for deleted item
      setValidationErrors((prev) => {
        if (prev[id]) {
          const next = { ...prev };
          delete next[id];
          return next;
        }
        return prev;
      });
      setValidationWarnings((prev) => {
        if (prev[id]) {
          const next = { ...prev };
          delete next[id];
          return next;
        }
        return prev;
      });
    },
    [validateAllItems]
  );

  /**
   * Handle reorder from SortableList
   */
  const handleReorder = useCallback(
    (reorderedItems: FinalCheckItem[]) => {
      setLocalItems(reorderedItems);
      validateAllItems(reorderedItems);
    },
    [validateAllItems]
  );

  // ============================================================================
  // Reset to Defaults
  // ============================================================================

  const handleResetToDefaults = useCallback(() => {
    // Deep copy from currentSettings
    const itemsCopy: FinalCheckItem[] = currentSettings.finalCheckItems.map((i) => ({
      id: i.id,
      label: i.label,
    }));

    setLocalItems(itemsCopy);
    setValidationErrors({});
    setValidationWarnings({});
    // Set checkbox to checked after reset (user explicitly chose to match defaults)
    setApplyToFuture(true);
    // Clear divergence warning since user explicitly chose to match defaults
    setWasInitiallyDivergent(false);
  }, [currentSettings]);

  // ============================================================================
  // Save Handler
  // ============================================================================

  const handleSave = useCallback(async () => {
    // Guard: currentSettings must be available
    if (!currentSettings) {
      showToast({
        title: 'Cannot save',
        description: 'Settings are not available. Please try again.',
        color: 'danger',
      });
      return;
    }

    // Validate all items and trim labels
    const trimmedItems: FinalCheckItem[] = localItems.map((item) => ({
      ...item,
      label: item.label.trim(),
    }));

    const { errors } = validateAllItems(trimmedItems);

    // If any blocking errors, abort save
    if (hasBlockingErrors(errors)) {
      setLocalItems(trimmedItems);
      showToast({
        title: 'Validation errors',
        description: 'Please fix the errors before saving.',
        color: 'warning',
      });
      return;
    }

    setIsSaving(true);

    try {
      // Build finalCheckItems for saving
      const finalCheckItemsSettings: PreTradingSettings['finalCheckItems'] =
        trimmedItems.map((item) => ({
          id: item.id,
          label: item.label,
        }));

      // Step 1: Update draft config
      const draftSuccess = await sessionDraftService.updateFinalCheckItemsConfig(
        draftId,
        finalCheckItemsSettings
      );

      if (!draftSuccess) {
        showToast({
          title: 'Save failed',
          description: 'Session draft not found or invalid.',
          color: 'danger',
        });
        setIsSaving(false);
        return;
      }

      // Step 2: If checkbox checked, update global settings
      let globalUpdateSucceeded = true;
      if (applyToFuture) {
        try {
          await preTradingSettingsService.update(currentSettings.id, {
            finalCheckItems: finalCheckItemsSettings,
          });
        } catch (err) {
          console.error('Failed to update global settings:', err);
          globalUpdateSucceeded = false;
          showToast({
            title: 'Partial save',
            description:
              "Changes saved to current session, but couldn't update defaults for future sessions.",
            color: 'warning',
          });
        }
      }

      // Step 3: Ack hash logic
      if (applyToFuture) {
        if (globalUpdateSucceeded) {
          // Global update succeeded - hash the new global settings
          const newGlobalSettings: PreTradingSettings = {
            ...currentSettings,
            finalCheckItems: finalCheckItemsSettings,
          };
          const ackHash = computeFinalCheckItemsHash(newGlobalSettings);
          await sessionDraftService.setFinalCheckItemsAckHash(draftId, ackHash);
        }
        // If global update failed, do NOT set ack hash, keep modal open
        if (!globalUpdateSucceeded) {
          setIsSaving(false);
          return; // Keep modal open for retry
        }
      } else {
        // Checkbox unchecked - set ack hash to current global (intentional divergence)
        const ackHash = computeFinalCheckItemsHash(currentSettings);
        await sessionDraftService.setFinalCheckItemsAckHash(draftId, ackHash);
      }

      // Step 4: Re-fetch updated draft to get migrated values
      const updatedDraft = await sessionDraftService.getById(draftId);
      if (updatedDraft && updatedDraft.preTradingConfig) {
        onSaveSuccess(
          updatedDraft.preTradingConfig,
          updatedDraft.finalChecks
        );
      }

      // Close modal on success
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      showToast({
        title: 'Save failed',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    currentSettings,
    localItems,
    applyToFuture,
    draftId,
    validateAllItems,
    showToast,
    onSaveSuccess,
    onClose,
  ]);

  // ============================================================================
  // Item Row Renderer
  // ============================================================================

  const renderItemRow = useCallback(
    (item: FinalCheckItem, dragHandleProps: DragHandleProps) => {
      const error = validationErrors[item.id]?.label;
      const warning = validationWarnings[item.id]?.label;
      const shouldAutoFocus = newlyAddedIdRef.current === item.id;

      return (
        <Card
          shadow="sm"
          radius="sm"
          className={dragHandleProps.isDragging ? 'opacity-50 shadow-lg' : ''}
        >
          <CardBody className="p-3">
            <div className="flex items-center gap-3">
              <DragHandle dragHandleProps={dragHandleProps} />

              {/* Inline Input */}
              <div className="flex-1 min-w-0">
                <Input
                  size="sm"
                  placeholder="e.g., Review key levels"
                  value={item.label}
                  onChange={(e) => handleItemChange(item.id, e.target.value)}
                  onBlur={(e) => handleItemBlur(item.id, e.target.value)}
                  isInvalid={!!error}
                  errorMessage={error || warning}
                  autoFocus={shouldAutoFocus}
                  classNames={{
                    input: 'text-base',
                    errorMessage: warning && !error ? 'text-warning' : undefined,
                  }}
                />
              </div>

              <Button
                isIconOnly
                size="sm"
                variant="light"
                color="danger"
                onPress={() => handleDeleteItem(item.id)}
                aria-label={`Delete ${item.label || 'item'}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </CardBody>
        </Card>
      );
    },
    [validationErrors, validationWarnings, handleItemChange, handleItemBlur, handleDeleteItem]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="top"
      scrollBehavior="inside"
      size="2xl"
      isDismissable={!isDirty && !isSaving}
      hideCloseButton={isDirty || isSaving}
      isKeyboardDismissDisabled={isDirty || isSaving}
    >
      <ModalContent>
        <ModalHeader>
          <span className="text-lg font-semibold">Configure Final Checks</span>
        </ModalHeader>
        <ModalBody className="gap-4">
          {/* Divergent State Warning - shows if draft was divergent when modal opened */}
          {wasInitiallyDivergent && (
            <Alert
              color="warning"
              title="This draft differs from your defaults"
              description="Your current edits won't affect this warning."
              endContent={
                <Button
                  size="sm"
                  variant="flat"
                  color="warning"
                  onPress={handleResetToDefaults}
                >
                  Reset to defaults
                </Button>
              }
            />
          )}

          {/* Items List */}
          {localItems.length > 0 ? (
            <SortableList
              items={localItems}
              onReorder={handleReorder}
              renderItem={renderItemRow}
              className="space-y-2"
            />
          ) : (
            <EmptyState
              title="No check items configured"
              description="Add items to create your pre-trading final checklist."
            />
          )}

          {/* Add Item Button */}
          <Button
            variant="flat"
            color="primary"
            startContent={<Plus className="w-4 h-4" />}
            onPress={handleAddItem}
            className="w-full md:w-auto"
          >
            Add Check Item
          </Button>

          {/* Apply to Future Sessions Checkbox - always visible */}
          <Checkbox
            isSelected={applyToFuture}
            onValueChange={setApplyToFuture}
            size="sm"
          >
            Apply to future sessions
          </Checkbox>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button color="primary" onPress={handleSave} isLoading={isSaving}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FinalChecksConfigModal;
